import { Big } from 'big.js'

import { castNumber } from './type.js'

import type { BigSource } from 'big.js'

type Input = string | number | null | undefined

/**
 * The type of transformation to apply to the value using {@link parseNumber}.
 */
export type Parser =
  | 'fixed1'
  | 'percentage'
  | 'negative'
  | 'currency'

/**
 * Tries to convert the provided input into a string representation of a number in the requested format.
 *
 * See {@link Parser} for details of the parsers available.
 */
export function parseNumber (value: Input, parser: Parser | undefined): string {
  switch (parser) {
    case 'fixed1':
      if (!value) return '0.0'
      return Big(value).toFixed(1)
    case 'percentage':
      if (!value) return '0'
      return Big(value).times(100).toString()
    case 'negative':
      if (!value) return '-0.00'
      return `-${value}`
    case 'currency':
      if (!value) return '0.00'
      return Big(value).toFixed(2)
    default:
      if (!value) return '0'
      return String(value)
  }
}

/**
 * Extracts a numeric value from formatted string input.
 */
export function formatNumberInput (
  value: Input,
  options: { decimalPercentage?: boolean, decimalPrecision?: number } = {},
): string | null {
  if (value === null || value === undefined || value === '') return null

  const { decimalPercentage = false, decimalPrecision } = options
  const sourceValue = String(value)
  const isNegative = typeof value === 'number' ? value < 0 : sourceValue.startsWith('-')
  const cleanValue = typeof value === 'number'
    ? Math.abs(value).toString()
    : sourceValue.replace(/[^\d.]/g, '')

  if (!cleanValue || cleanValue === '.') return null

  const numericValue = typeof value === 'number' ? big(value).abs() : big(cleanValue)
  let parsedValue = numericValue.toString()

  if (decimalPercentage) {
    parsedValue = numericValue.div(100).toString()
  }

  parsedValue = decimalPrecision !== undefined
    ? big(parsedValue).toFixed(decimalPrecision)
    : parsedValue

  return isNegative ? `-${parsedValue}` : parsedValue
}

/**
 * Adds all the provided values together.
 */
export function bigSum (...args: Input[]): Big {
  return [...args].reduce((acc, arg) => acc.plus(arg || 0), Big(0))
}

/**
 * Starting with initial value, subtracts the remaining values from it.
 */
export function bigSubtract (initial: Input, ...args: Input[]): Big {
  return [...args].reduce((acc, arg) => acc.minus(arg || 0), Big(initial || 0))
}

/**
 * Multiplies all the provided values together.
 */
export function bigMultiply (...args: Input[]): Big {
  return [...args].reduce((acc, arg) => acc.times(arg || 0), Big(1))
}

/**
 * Convenience method for creating a {@link Big} instance with the provided value.
 */
export function big (value: BigSource): Big {
  return Big(value)
}

/**
 * Safely tries to create a {@link Big} instance with the provided value.
 * Returns the default value if unable to convert.
 */
export function bigSafe (value: BigSource | null | undefined, defaultValue = 0): Big {
  try {
    return Big(value ?? defaultValue)
  } catch {
    return Big(defaultValue)
  }
}

/**
 * Tries to cast the 2 provided values to a number, then runs them through the compare function provided.
 * If values are unable to be cast will do a comparison of the original values.
 */
export function preferNumberCompare<V1, V2> (
  val1: V1,
  val2: V2,
  compareFn: (val1: V1 | number, val2: V2 | number) => boolean,
): boolean {
  const parsedVal1 = castNumber(val1)
  const parsedVal2 = castNumber(val2)
  return compareFn(parsedVal1, parsedVal2)
}
